// Piezas comunes de los modelos de sensores: placas con serigrafía, conectores, componentes SMD,
// trimmers, LEDs de módulo y texturas compartidas (malla de transductor, lente Fresnel, rejillas).
import * as THREE from 'three';
import { Builder, M, mat, canvas, texture, text, glow, setGlow, emissiveMat, extrudeBoard, boardShape, cachedGeo } from '../kit.js';
import { PITCH } from '../../parts/registry.js';

export { Builder, M, mat, canvas, texture, text, glow, setGlow, emissiveMat, extrudeBoard, boardShape, cachedGeo, PITCH, THREE };

const texCache = new Map();
const matCache = new Map();

/** Geometría compartida (caché global de kit.js con prefijo propio); no se libera con la pieza. */
export function sgeo(key, make) {
  return cachedGeo('sns:' + key, () => {
    const g = make();
    g.userData.shared = true;
    return g;
  });
}

/** Textura compartida entre todas las instancias (no se libera al borrar una pieza). */
export function sharedTex(key, make) {
  let t = texCache.get(key);
  if (!t) {
    t = make();
    t.userData.shared = true;
    texCache.set(key, t);
  }
  return t;
}

/** Material compartido (no se libera al borrar una pieza). */
export function sharedMat(key, make) {
  let m = matCache.get(key);
  if (!m) {
    m = make();
    m.userData.shared = true;
    matCache.set(key, m);
  }
  return m;
}

export const COLORS = {
  pcbBlue: '#1b4c9a',
  pcbGreen: '#1e6b37',
  pcbBlack: '#18191c',
  pcbRed: '#a81d1a',
  silk: '#f2f4f2',
  pad: '#d4d6d8',
};

/**
 * Serigrafía de una placa (w × d mm, vista desde arriba con −Z arriba). draw(g, X, Y) dibuja en mm;
 * X(x) y Y(z) convierten coordenadas de la placa. Los agujeros quedan transparentes.
 */
export function silkTexture(key, w, d, color, draw, { holes = [], s = 18 } = {}) {
  return sharedTex(key, () => {
    const { c, g } = canvas(w, d, s);
    const X = (x) => x + w / 2;
    const Y = (z) => z + d / 2;
    g.fillStyle = color;
    g.fillRect(0, 0, w, d);
    draw(g, X, Y);
    g.globalCompositeOperation = 'destination-out';
    for (const [hx, hz, hr] of holes) {
      g.beginPath();
      g.arc(X(hx), Y(hz), hr, 0, Math.PI * 2);
      g.fill();
    }
    g.globalCompositeOperation = 'source-over';
    return texture(c);
  });
}

/** Placa horizontal: cuerpo extruido con agujeros y calcomanía de serigrafía en la cara superior. */
export function flatBoard(root, { key, w, d, t = 1.6, y0, x = 0, z = 0, color, holes = [], shape = null, draw = () => {}, r = 0.6 }) {
  const sh = shape || boardShape(w, d, { r });
  const pcb = extrudeBoard(sh, t, null, color, { holes, w, d, y0 });
  pcb.position.x += x;
  pcb.position.z += z;
  root.add(pcb);
  const tex = silkTexture(key, w, d, color, draw, { holes });
  const m = sharedMat('silk:' + key, () => new THREE.MeshStandardMaterial({ map: tex, alphaTest: 0.5, roughness: 0.5, metalness: 0.05 }));
  const top = new THREE.Mesh(sgeo(`plane-top${w},${d}`, () => new THREE.PlaneGeometry(w, d).rotateX(-Math.PI / 2)), m);
  top.position.set(x, y0 + t + 0.02, z);
  top.receiveShadow = true;
  root.add(top);
  return pcb;
}

/** Pads de cobre estañados y texto para la serigrafía. */
export function pad(g, x, y, r = 0.8, hole = 0.45) {
  g.fillStyle = COLORS.pad;
  g.beginPath();
  g.arc(x, y, r, 0, Math.PI * 2);
  g.fill();
  g.fillStyle = '#5b5f63';
  g.beginPath();
  g.arc(x, y, hole, 0, Math.PI * 2);
  g.fill();
}

export function silkText(g, str, x, y, size = 1.2, opts = {}) {
  text(g, str, x, y, { size, color: COLORS.silk, weight: 700, ...opts });
}

/** Pistas de cobre bajo la máscara (un poco más claras que la placa). */
export function traces(g, color, paths, width = 0.35) {
  g.strokeStyle = color;
  g.lineWidth = width;
  g.lineCap = 'round';
  g.lineJoin = 'round';
  for (const p of paths) {
    g.beginPath();
    g.moveTo(p[0][0], p[0][1]);
    for (let i = 1; i < p.length; i++) g.lineTo(p[i][0], p[i][1]);
    g.stroke();
  }
}

// Conectores ------------------------------------------------------------------------------------------

/**
 * Conector macho en ángulo recto acostado sobre una placa horizontal: el plástico queda sobre la placa
 * al ras del borde xEdge y los pines salen en dirección dir (−1 = hacia −X) hasta tipX, a 1.27 mm de
 * altura sobre la placa. Los extremos doblados entran a la placa detrás del plástico.
 */
export function headerRightAngle(b, { n, xEdge, top, tipX, t = 1.6, dir = -1 }) {
  const pinY = top + 1.27;
  b.box(2.5, 2.54, n * PITCH, M.blackPlastic, xEdge - dir * 1.25, top, 0);
  const bend = xEdge - dir * 3.6;
  for (let i = 0; i < n; i++) {
    const z = (i - (n - 1) / 2) * PITCH;
    b.box(Math.abs(bend - tipX), 0.64, 0.64, M.gold, (bend + tipX) / 2, pinY - 0.32, z);
    b.box(0.64, pinY - (top - t - 0.7), 0.64, M.gold, bend, top - t - 0.7, z);
  }
}

/** Conector macho recto bajo una placa (patas hacia abajo para la protoboard). */
export function headerDown(b, { xs, z, bottom, t }) {
  const x0 = Math.min(...xs) - PITCH / 2;
  const x1 = Math.max(...xs) + PITCH / 2;
  b.box(x1 - x0, 2.5, PITCH, M.blackPlastic, (x0 + x1) / 2, bottom - 2.5, z);
  for (const x of xs) {
    b.box(0.64, bottom + t + 0.9, 0.64, M.gold, x, 0, z);
    b.cyl(0.45, 1.0, 0.6, M.tin, x, bottom + t, z, 10);
  }
}

// Componentes ------------------------------------------------------------------------------------------

/** Circuito integrado SOIC (cuerpo y patas). y: cara de la placa; under: cuelga bajo la placa. */
export function soic(b, x, y, z, pins = 8, { rot = 0, under = false } = {}) {
  const n = pins / 2;
  const len = n * 1.27 + 0.5;
  const bw = rot ? 3.9 : len;
  const bd = rot ? len : 3.9;
  b.box(bw, 1.45, bd, M.chip, x, under ? y - 1.6 : y + 0.15, z);
  const ly = under ? y - 0.95 : y + 0.7;
  for (let i = 0; i < n; i++) {
    const o = (i - (n - 1) / 2) * 1.27;
    for (const side of [-1, 1]) {
      if (rot) b.box(1.2, 0.25, 0.42, M.tin, x + side * 2.4, ly, z + o);
      else b.box(0.42, 0.25, 1.2, M.tin, x + o, ly, z + side * 2.4);
    }
  }
}

/** Resistencia o capacitor SMD 0805. kind: 'r' (negro) o 'c' (beige). */
export function smd(b, x, y, z, { kind = 'r', rot = 0, under = false } = {}) {
  const body = kind === 'r' ? M.black : mat('#b98b5c', { roughness: 0.6 });
  const yy = under ? y - 0.5 : y;
  if (rot) {
    b.box(1.25, 0.5, 1.3, body, x, yy, z);
    for (const s of [-1, 1]) b.box(1.27, 0.52, 0.36, M.tin, x, yy, z + s * 0.83);
  } else {
    b.box(1.3, 0.5, 1.25, body, x, yy, z);
    for (const s of [-1, 1]) b.box(0.36, 0.52, 1.27, M.tin, x + s * 0.83, yy, z);
  }
}

/** Trimmer cuadrado tipo 3362 (6.6 mm) con rotor redondo ranurado en cruz. Devuelve el rotor. */
export function trimmer3362(b, x, y, z, { color = '#2a63c8', rotor = '#f1efe6', under = false, angle = 0 } = {}) {
  const h = 4.4;
  const bodyMat = mat(color, { roughness: 0.45 });
  b.box(6.6, h - 0.8, 6.6, bodyMat, x, under ? y - h + 0.8 : y, z);
  const g = new THREE.Group();
  const rm = mat(rotor, { roughness: 0.5 });
  const disc = new THREE.Mesh(sgeo('trim-rotor', () => new THREE.CylinderGeometry(2.7, 2.7, 0.8, 24)), rm);
  const slotA = new THREE.Mesh(sgeo('trim-slot', () => new THREE.BoxGeometry(4.2, 0.4, 0.8)), M.darkPlastic);
  const slotB = slotA.clone();
  slotB.rotation.y = Math.PI / 2;
  disc.castShadow = true;
  g.add(disc, slotA, slotB);
  if (under) {
    g.position.set(x, y - h + 0.4, z);
    slotA.position.y = slotB.position.y = -0.25;
  } else {
    g.position.set(x, y + h - 0.4, z);
    slotA.position.y = slotB.position.y = 0.25;
  }
  g.rotation.y = angle;
  b.root.add(g);
  return g;
}

/** Trimmer multivuelta 3296W (azul, 9.5 × 4.8 × 10 mm) con tornillo de latón. */
export function trimmer3296(b, x, y, z) {
  b.box(9.5, 9.8, 4.8, mat('#1f5bd0', { roughness: 0.45 }), x, y, z);
  b.cyl(1.15, 1.15, 0.9, M.gold, x + 3.2, y + 9.8, z, 16);
  b.box(2.0, 0.2, 0.35, M.darkPlastic, x + 3.2, y + 10.7, z);
  for (const dx of [-2.54, 0, 2.54]) b.box(0.5, 0.6, 0.5, M.tin, x + dx, y - 0.4, z);
}

/** LED SMD 0805 de módulo: devuelve { set(b) } para encenderlo (0–1). */
export function smdLed(root, x, y, z, color, { under = false } = {}) {
  const body = new THREE.Mesh(sgeo('smd-led-base', () => new THREE.BoxGeometry(2.0, 0.45, 1.25)), mat('#e9e7df', { roughness: 0.5 }));
  body.position.set(x, under ? y - 0.23 : y + 0.23, z);
  root.add(body);
  const m = emissiveMat(new THREE.Color(color).multiplyScalar(0.35), color, { roughness: 0.3 });
  const lens = new THREE.Mesh(sgeo('smd-led-lens', () => new THREE.BoxGeometry(1.2, 0.4, 1.05)), m);
  lens.position.set(x, under ? y - 0.65 : y + 0.65, z);
  root.add(lens);
  const halo = glow(color, 7);
  halo.position.set(x, under ? y - 1.4 : y + 1.6, z);
  root.add(halo);
  return {
    set(v) {
      m.emissiveIntensity = v * 14;
      setGlow(halo, v * 0.9);
    },
  };
}

/** Pata recta de componente (alambre estañado). */
export function lead(b, pts, r = 0.22) {
  b.leg(pts, r, M.tin);
}

// Texturas compartidas -----------------------------------------------------------------------------

/** Malla metálica de los transductores ultrasónicos y del micrófono. */
export function meshTexture(base = '#2b2d30', wire = '#55595f') {
  return sharedTex('mesh' + base + wire, () => {
    const c = document.createElement('canvas');
    c.width = c.height = 128;
    const g = c.getContext('2d');
    g.fillStyle = base;
    g.fillRect(0, 0, 128, 128);
    g.strokeStyle = wire;
    g.lineWidth = 1.4;
    for (let i = 0; i <= 128; i += 4) {
      g.beginPath();
      g.moveTo(i, 0);
      g.lineTo(i, 128);
      g.stroke();
      g.beginPath();
      g.moveTo(0, i);
      g.lineTo(128, i);
      g.stroke();
    }
    const t = texture(c, { aniso: 8 });
    return t;
  });
}

/** Lente de Fresnel del PIR: facetas en anillos (coordenadas UV de la esfera; v = 1 en el polo). */
export function fresnelTexture() {
  return sharedTex('fresnel', () => {
    const c = document.createElement('canvas');
    c.width = 512;
    c.height = 256;
    const g = c.getContext('2d');
    g.fillStyle = '#efeee8';
    g.fillRect(0, 0, 512, 256);
    const rows = 4;
    for (let r = 0; r < rows; r++) {
      const y0 = (r / rows) * 256;
      const y1 = ((r + 1) / rows) * 256;
      const cols = 4 + r * 4;
      for (let k = 0; k < cols; k++) {
        const x0 = (k / cols) * 512 + (r % 2 ? 256 / cols : 0);
        const w = 512 / cols;
        // Cada faceta: centro claro y bordes más oscuros (lente de segmentos)
        const grad = g.createLinearGradient(x0, y0, x0 + w, y1);
        grad.addColorStop(0, '#f7f6f1');
        grad.addColorStop(0.55, '#e6e5de');
        grad.addColorStop(1, '#d3d2ca');
        g.fillStyle = grad;
        g.fillRect(x0 + 1.5, y0 + 1.5, w - 3, y1 - y0 - 3);
        if (x0 + w > 512) g.fillRect(x0 - 512 + 1.5, y0 + 1.5, w - 3, y1 - y0 - 3);
      }
      g.fillStyle = 'rgba(150,150,140,0.7)';
      g.fillRect(0, y1 - 1.5, 512, 3);
    }
    return texture(c, { aniso: 8 });
  });
}
